import { Component, ComponentFactoryResolver, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { AbstractModalContentComponent } from '@ibe.components/common/modal/abstract-modal-content.component';
import { ModalService } from '@ibe.components/common/modal/modal.indra.service';
import { takeWhile } from 'rxjs/operators';
import scrollLock from 'scroll-lock';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromStore from '../../../store';

@Component({
	selector: 'app-modal',
	templateUrl: './modal.indra.component.html',
	styleUrls: ['./modal.component.scss'],
	host: { class: 'app-modal' }
})
export class INDRAModalComponent implements OnInit, OnDestroy {
	@ViewChild('modal__content', { read: ViewContainerRef })
	$modalContent: ViewContainerRef;
	@ViewChild('modal') $modalElem: ElementRef;

	@HostBinding('class.is-open')
	isOpen: boolean;

	@HostBinding('attr.aria-hidden')
	isAriaHidden: boolean;

	isAlive = true;
	content: Type<any> = null;
	lastActiveElement;
	params: any[] = null;
	modalType: '' | 'dialog' | 'full' | 'large' | 'payment';
	
	closable: boolean = true;
	closable$: Observable<boolean> = this.store.select(fromStore.getShowClose);
	
	htmlElement = document.getElementsByTagName('html');

	destroy: Function;
	multiples = false;

	openModalTimeout;

	constructor(
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private router: Router,
		private focusTrap: FocusTrapFactory,
		private store: Store<fromStore.State>
	) {
		this.router.events
			.pipe(takeWhile(() => this.isAlive))
			.subscribe(event => {
				if (event instanceof NavigationStart) {
					if (this.isOpen) {
						window.history.forward();
						// handleOverlayClick to handleClose to avoid logging out from stop over modal
						this.handleClose();
					}
				}
			});
		
		/*	note: post-sprint 8 client code merge*/
		this.closable$
			.pipe(takeWhile(() => this.isAlive))
			.subscribe(value => {
				this.closable = value;
			});
		
	}

	/**
	 * Keyboard listener
	 * Dismiss with Escape key
	 */
	@HostListener('window:keyup', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
		event.stopPropagation();
		if (this.isOpen) {
			if (event.key === 'Escape' && (this.closable == undefined || this.closable )) {
				this.handleClose();
			}
		}
	}

	ngOnInit() {
		const self = this;
		this.multiples = false;

		this.modalService.isOpen
			.pipe(takeWhile(() => this.isAlive && !this.multiples))
			.subscribe(
				value => {
					this.isOpen = value;

					if (this.isOpen) {
						this.lastActiveElement = document.activeElement;
						this.$modalElem.nativeElement.focus();
					} else if (this.lastActiveElement !== undefined) {
						this.lastActiveElement.focus();
					}

					// execute when closing modal to show sticky calendar header
					if (!this.isOpen) {
						this.htmlElement[0].classList.remove('no-scroll');
						scrollLock.show();
					}

					this.isAriaHidden = !value;
				},
				error => console.error(error),
				() => { }
			);

		this.modalService.hostedComponent
			.pipe(takeWhile(() => this.isAlive && !this.multiples))
			.subscribe(
				value => {
					this.content = value;
					this.params = self.modalService.params;
					this.loadComponent();
				},
				error => console.error(error),
				() => { }
			);
	}

	ngOnDestroy() {
		this.isAlive = false;
	}

	loadComponent() {
		this.$modalContent.clear();

		if (this.content) {
			const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.content);
			const componentRef = this.$modalContent.createComponent(componentFactory);
			(<AbstractModalContentComponent>componentRef.instance).data = this.params;
			this.modalType = (<AbstractModalContentComponent>componentRef.instance).TYPE;
			this.closable = (<AbstractModalContentComponent>componentRef.instance).CLOSABLE; // removed in post-sprint 8 client code delivery

			this.open();
		}
	}

	loadComponentMultiples() {
		this.$modalContent.clear();
		this.multiples = true;

		if (this.content) {
			const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.content);
			const componentRef = this.$modalContent.createComponent(componentFactory);
			(<AbstractModalContentComponent>componentRef.instance).data = this.params;
			this.modalType = (<AbstractModalContentComponent>componentRef.instance).TYPE;
			(<AbstractModalContentComponent>componentRef.instance).destroy = () => { this.destroy(); };

			this.open();
		}
	}

	/* Puts focus on it and trap navigation */
	applyA11y() {
		let focusTrap = this.focusTrap.create(this.$modalElem.nativeElement); // creates a focus trap region
		focusTrap.focusInitialElement(); // Moves the focus in the form (by default the first field)
	}

	open() {
		this.isOpen = true;
		this.isAriaHidden = true;

		// Disable scroll on body - Multimodal
		this.htmlElement[0].classList.add('no-scroll');
		scrollLock.hide();

		// to wait for modal
		clearTimeout(this.openModalTimeout);
		this.openModalTimeout = setTimeout(() => {
			this.applyA11y();
			this.handleFocus();
		}, 500);
	}

	handleFocus() {
		if (this.isOpen) {
			this.lastActiveElement = document.activeElement;
			this.$modalElem.nativeElement.focus();
		}
	}

	handleClose() {
		this.lastActiveElement.focus();
		if (this.destroy) this.destroy();
		this.modalService.close();
	}

	handleOverlayClick() {
		this.modalService.handleOverlayClick();
		// Added condition for paybyrdModalComponent and adyenModalComponent, so it will not be closed when clicking outside the modal.
		const paymentModal = this.params[0].component === "paybyrdModalComponent" || this.params[0].component === "adyenModalComponent" ? true : false;
		if ((this.closable == undefined || this.closable) && !paymentModal ) this.handleClose();
	}
}
